import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { AuthProvider } from '../services/auth.providers';
import { catchError, filter } from 'rxjs/operators';
import { AvailableFeatureFlags, FeatureFlagModel } from '@smartmoving/sm-shared-lib';
import { RegionService } from 'src/app/core/region.service';

@Injectable()
export class AuthorizationHttpInterceptor implements HttpInterceptor {
  private useBetaUrl = false;
  private hasCheckedBetaFlag = false;
  private cacheBust = '';

  constructor(private readonly injector: Injector) {
    this.setCacheBust();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const auth = this.injector.get(AuthProvider);

    const headers = req.headers;
    if (headers.has('skipAuth')) {
      // removing this header before forwarding request
      const interceptOptions = {
        headers: req.headers.delete('skipAuth')
      };
      req = req.clone(interceptOptions);
      return next.handle(req);
    }

    if (req.url.includes(auth.authURL)) {
      // On a physical device, not as simple as "refresh the page", so lets reset everything if they're logging in.
      this.hasCheckedBetaFlag = false;
      this.useBetaUrl = false;
      this.setCacheBust();
    }

    if (auth.getToken() && !this.hasCheckedBetaFlag) {
      // Set the flag immediately, which prevents recursion
      this.hasCheckedBetaFlag = true;
      const httpClient = this.injector.get(HttpClient);
      const regionService = this.injector.get(RegionService);
      httpClient.get<FeatureFlagModel>(`${regionService.getApiBaseUrl()}/features/has-access-to/${AvailableFeatureFlags.BetaFeatures}?cacheBust=${this.cacheBust}`)
          .pipe(filter(x => x.hasAccess))
          .subscribe(() => this.runBetaCheck(httpClient));
    }

    const interceptorOptions = {
      url: (req.url.includes('http')) ? req.url : this.getUrl(req.url, auth.authURL),
      headers: req.headers.set('Authorization', `Bearer ${auth.getToken()}`)
    };

    // Need this header when checking in native app from local.
    if (interceptorOptions.url.includes('ngrok')) {
      interceptorOptions.headers = interceptorOptions.headers.set('ngrok-skip-browser-warning', 'true');
    }

    const authReq = req.clone(interceptorOptions);
    return next
        .handle(authReq).pipe(catchError(err => {
          if (!authReq.url.includes(auth.authURL) && err.status === 401) {
            auth.logout();
          }
          return throwError(err);
        }));
  }

  private setCacheBust() {
    this.cacheBust = (Math.random() + 1).toString(36).substring(7);
  }

  // Created by Claude Code AI Agent
  // Multi-region: route requests to the regional API resolved by RegionService.
  // Falls back to environment.apiUrl/apiBetaUrl when no region is set — logins
  // from before the multi-region rollout continue to work. Only relative URLs
  // pass through here; crew-api calls are absolute and resolve their region in
  // CrewApiClientService instead.
  private getUrl(urlFragment: string, authUrl: string) {
    const regionService = this.injector.get(RegionService);
    if (urlFragment.includes(authUrl)) {
      // To be safe, always use the old API when logging in, so they can reset reliably if needed.
      return `${regionService.getApiBaseUrl()}/${urlFragment}`;
    }

    return `${this.useBetaUrl ? regionService.getApiBetaBaseUrl() : regionService.getApiBaseUrl()}/${urlFragment}`;
  }

  private runBetaCheck(httpClient: HttpClient) {
    const regionService = this.injector.get(RegionService);
    httpClient.get(`${regionService.getApiBetaBaseUrl()}/ping?cacheBust=${this.cacheBust}`,
        { responseType: 'text' })
        .subscribe({
          next: () => this.useBetaUrl = true,
          // If it's blocked by their firewall, or any other issue, just fallback to the normal API.
          error: () => this.useBetaUrl = false,
        });
  }
}
